import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, switchMap } from 'rxjs/operators';
import { ITitleProviderService } from '@nimbus-library/features/menu/services/contracts/i-title-provider.service';
import { IPageMetadata } from '@nimbus-library/features/menu';
import { NimbusAdminRoutes } from '@nimbus-admin/routing/nimbus-admin.routes';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { LocalizationState } from '@nimbus-library/features/localization/store/localization.state';
import { Language } from '@nimbus-library/features/localization/models/enums/language.enum';

@Injectable()
export class TitleProviderService extends ITitleProviderService {
  private readonly prefix: string = 'Nimbus';

  protected titles: IMap<IPageMetadata> = {
    [`/${NimbusAdminRoutes.Overview}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuOverview) },
    [`/${NimbusAdminRoutes.Partners}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuPartners) },
    [`/${NimbusAdminRoutes.Tenants}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuTenants) },
    [`/${NimbusAdminRoutes.Workflow}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuWorkflow) },
    [`/${NimbusAdminRoutes.Settings}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuSettings) },
    [`/${NimbusAdminRoutes.Teams.root}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuTeams) },
    [`/${NimbusAdminRoutes.Users}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuUsers) },
    [`/${NimbusAdminRoutes.Operations.root}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuOperations) },
    [`/${NimbusAdminRoutes.ExternalTools}`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuExternalTools) },
    [`/${NimbusAdminRoutes.ExternalTools}/:linkId`]: { title$: () => this.getTranslatedTitle(TranslationKeys.MenuExternalTools) },
    '*': { title$: () => this.getTranslatedTitle() },
  };

  public constructor(
    private readonly translateService: TranslateService,
    private readonly store: Store,
  ) {
    super();
  }

  private getTranslatedTitle(translationKey?: TranslationKeys): Observable<string> {
    if (!translationKey) {
      return of(this.prefix);
    }

    return this.store.select(LocalizationState.selectedLanguage).pipe(
      switchMap(() => this.translateService.get(translationKey)),
      skipWhile(
        (translation) => translation === translationKey && this.store.selectSnapshot(LocalizationState.selectedLanguage) !== Language.Debug,
      ),
      distinctUntilChanged(),
      map((translation) => `${this.prefix} - ${translation}`),
    );
  }
}
